import { useCallback, useEffect, useState } from "react";
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from "../contexts/UserContext";
import Request from '../types/Request';
import FetchError from '../types/Error';
import Loader from '../components/Loader';
import NotFound from "../pages/NotFound";
import { ModalWrapper, PullToRefreshModalWrapper }  from "../components/ModalWrapper";
import { RequestMap, OwnRequestMap } from '../components/RequestMap';
import { RequestModalProps } from "../types/Props";
import { 
  getRequest, 
  addViewToRequest, 
  deleteRequest, 
  updateRequestDescription, 
  addRequestResponder, 
  setRequestReady,
  removeRequestResponder,
  updateRequestLocation
} from "../api/requests";
import { useUserLocation } from "../utils/locationUtils";
import { isValidInput, isValidLength } from "../utils/validateInput";
import { isUserAdmin } from "../utils/authUtils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from "../utils/icons";
import { formatDistanceStrict } from 'date-fns';
import { enUS, fi, sv } from 'date-fns/locale';

const RequestModal = ({ onClose, isUserRequest, request_id, onUpdate, triggerRefresh }: RequestModalProps) => {
  const { translations, language } = useLanguage();
  const { user } = useUser();
  const [request, setRequest] = useState<Request>();
  const [originalRequest, setOriginalRequest] = useState<Request>();
  const checkIsUserRequest = Boolean(isUserRequest); // isUserRequest is 0 or 1, convert to boolean
  const [loading, setLoading] = useState<boolean>(true);
  const [descSuccess, setDescSuccess] = useState<string | null>(null);
  const [locationSuccess, setLocationSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [descError, setDescError] = useState<string | null>(null);
  const [locError, setLocError] = useState<string | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false); 
  const [isAccepted, setIsAccepted] = useState(true);
  const { userLatitude, userLongitude } = useUserLocation();
  const [showReadyConfirmation, setShowReadyConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [alreadyResponded, setAlreadyResponded] = useState(false);
  const [alreadyHasResponder, setAlreadyHasResponder] = useState(false);
  const [locationOption, setLocationOption] = useState('');
  const [gotHelp, setGotHelp] = useState<boolean | null>(null);
  const maxLength = 500;
  const descriptionPattern = /^[a-zA-ZäöÄÖ0-9\s.,!?:-]*$/;

  const statusColor = {
    'new': 'status-red',
    'in progress': 'status-yellow',
    'completed': 'status-green'
  }

  const fetchRequestData = useCallback(async () => {
    try {
      const data = await getRequest(request_id);
      setRequest(data);
      setOriginalRequest(data);
      setIsAccepted(data.status !== 'new');
    } catch (error) {
      const fetchError = error as FetchError;
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  }, [request_id]);

  useEffect(() => {
    fetchRequestData();
  }, [fetchRequestData]);

  // Add view to request if request is not user's own
  useEffect(() => {
    if (!isUserRequest && user) {
      addViewToRequest(request_id);
    }
  }, [request_id, isUserRequest, user]);

  // Initialize isApproximate's value
  useEffect(() => {
    if (request?.isApproximate === true) {
      setLocationOption("approximate")
    } else {
      setLocationOption("exact")
    }
  }, [request]);

  if (loading || !user ) return <Loader />;

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    setDescSuccess(null);
  };

  const handleUpdateDesc = async () => {
    setDescError('');
    if (!request?.description) {
      return;
    }

    if (!isValidLength(request.description ?? '', 1, maxLength)) {
      setDescError(translations.errors.invaliddesclength);
      return;
    }

    if (!isValidInput(request.description ?? '', descriptionPattern)) {
      setDescError(translations.errors.invalidcharacters);
      return;
    }

    try {
      await updateRequestDescription(request_id, request.description );
      fetchRequestData();
      onUpdate();
      setDescSuccess(translations.successes.descupdated);
      setIsEditMode(false);
    } catch (error) {
      const err = error as FetchError;
      setError(err.message);
    }
  };

  const handleUpdateLocation = async () => {
    let isApproximate;
    if (locationOption === "exact") {
      isApproximate = false
    } else {
      isApproximate = true
    }
    if (userLatitude && userLongitude) {
      try {
        await updateRequestLocation(request_id, userLatitude, userLongitude, isApproximate);
        setLocationSuccess(translations.successes.locationupdated);
        fetchRequestData();
        onUpdate();
      } catch (error) {
        setError(translations.errors.genericerror);
      }
    } else {
      setLocError(translations.errors.locationfailed);
    }
  };

  const handleAccept = async () => {
    setIsAccepted(true);
    try {
      await addRequestResponder(request_id);
      setShowAcceptConfirmation(false);
      fetchRequestData();
      onUpdate();
    } catch (error) {
      const err = error as FetchError;
      if (err.message === 'You have already responded to this request.') {
        setAlreadyResponded(true);
      } else if (err.message === 'This request already has a responder.') {
        setAlreadyHasResponder(true);
      } else if (err.message === 'You have reached the limit of 2 active responses.') {
        setLimitError(translations.errors.responselimitreached);
      } else {
        setError(err.message);
      }
      setShowAcceptConfirmation(false);
    }
  };

  const removeResponder = async () => {
    const removedBy = isUserRequest ? 'owner' : 'responder';
    try {
      await removeRequestResponder(request_id, removedBy);
      setShowRemoveConfirmation(false);
      fetchRequestData();
      onUpdate();
    } catch (error) {
      setError(translations.errors.genericerror);
    }
  };

  const handleReady = async () => {
    if (gotHelp === null) return;
    try {
      await setRequestReady(request_id, gotHelp);
      setShowReadyConfirmation(false);
      fetchRequestData();
      onUpdate();
    } catch (error) {
      setError(translations.errors.genericerror);
    }
  };

  const handleCancel = async () => {
    setRequest(originalRequest);
    setIsEditMode(false);
    setDescError('');
  };

  const handleDelete = async () => {
    try {
      await deleteRequest(request_id);
      onUpdate();
      triggerRefresh();
      onClose();
    } catch (error) {
      setError(translations.errors.genericerror);
    }
  };

  return (
    <>
      {request ? (
        <PullToRefreshModalWrapper title={translations[request.category]} onClose={onClose} onRefresh={fetchRequestData}>
          {error && <div className="error">{error}</div>}
          {request.is_test && <div className="test-request">Testipyyntö</div>}
          <p className="intertitle">{translations.basicinfo}</p>
          <div className="card">
            <div className="request-user-and-status">
              <div>
                <div className="spacer1">
                  <div><FontAwesomeIcon icon={icons.user} className="listIcon"/>{request.user_name}</div>
                </div>
                { request.status !== 'completed' && (
                  <div>
                    <FontAwesomeIcon icon={icons.phone} className="listIcon"/>
                    <a href={`tel:${request.phone}`} className="phonelink">{request.phone}</a>
                  </div>
                )}
              </div>
              <div>
                <small>
                  {translations.status}: {translations[request.status]}
                  <span className={`status-circle modal-circle ${statusColor[request.status] || ''}`}></span>
                </small>
              </div>
            </div>
            <div>
              <small>
                <div className="spacer2">
                  <FontAwesomeIcon icon={icons.rotateRight} className='listIcon'/>
                  <span className='text-spacer'>{translations.lastupdated}</span>
                  {formatDistanceStrict(new Date(request.updated_at), new Date(), { 
                    addSuffix: true, 
                    locale: language === 'finnish' ? fi : language === 'swedish' ? sv : enUS 
                  })}
                </div>
                <div className="spacer2">
                  <FontAwesomeIcon icon={icons.upload} className="listIcon"/>
                  <span className='text-spacer'>{translations.created}</span>
                  {formatDistanceStrict(new Date(request.created_at), new Date(), { 
                    addSuffix: true, 
                    locale: language === 'finnish' ? fi : language === 'swedish' ? sv : enUS 
                  })}
                </div>
                { (checkIsUserRequest || isUserAdmin()) &&
                  <div>
                    <FontAwesomeIcon icon={icons.eye} className='listIcon'/>
                    <span>{translations.views}: {request.views}</span>
                  </div>
                }
              </small>
            </div>
          </div>
          <p className="intertitle">{translations.location}</p>
          { request.latitude && request.longitude ? (
            checkIsUserRequest ? (
              <div className="card">
                <OwnRequestMap 
                  latitude={request.latitude} 
                  longitude={request.longitude} 
                  isApproximate={request.isApproximate}
                />
              </div>
            ) : (
              <div className="card">
                <RequestMap
                  latitude={request.latitude} 
                  longitude={request.longitude} 
                  userLatitude={userLatitude} 
                  userLongitude={userLongitude} 
                  isApproximate={request.isApproximate}
                />
              </div>
            )
          ) : (
            <div className="card">{translations.locationnotshared}</div>
          )}
          { checkIsUserRequest ? (
            <div>
              { request.status !== "completed" && (request.latitude && request.longitude) &&
                <div>
                  <div className="select-wrapper select-wrapper-location-request">
                    <select 
                      name="location" 
                      className="dropdown" 
                      onChange={(e) => setLocationOption(e.target.value)}
                      value={locationOption}
                      required
                    >
                      <option value="approximate">{translations.showapproximate}</option>
                      <option value="exact">{translations.showlocation}</option>
                    </select>
                  </div>
                  <button className="big-button update-location" onClick={handleUpdateLocation}>
                    <FontAwesomeIcon icon={icons.locationDot}/> {translations.updatelocation}
                  </button>
                </div>
              }
              { locationSuccess && <div className="success">{locationSuccess}</div> }
              { locError && <div className="error">{locError}</div> }
              { request.responder_user_name && request.status !== 'completed' && (
                <div>
                  <p className="intertitle">{translations.requestresponder}</p>
                  <div className="card responder-card">
                    <div className="responder-info">
                      <div>
                        <FontAwesomeIcon icon={icons.user} className="listIcon"/>{request.responder_user_name}
                      </div>
                      <div>
                        <FontAwesomeIcon icon={icons.phone} className="listIcon"/>
                        <a href={`tel:${request.responder_phone}`} className="phonelink">{request.responder_phone}</a>
                      </div>
                    </div>
                    <button className="big-button removeresponder" onClick={() => setShowRemoveConfirmation(true)}>
                    <FontAwesomeIcon icon={icons.ban}/> {translations.remove}
                    </button>
                  </div>
                </div>
              )}
              { request.status !== 'completed' ? (
              <div>
                <p className="intertitle">{translations.description}</p>
                <div className="card">
                  <textarea
                    name="description" 
                    rows={4} 
                    cols={40} 
                    value={request.description}
                    disabled={!isEditMode}
                    maxLength={maxLength}
                    onChange={(e) => setRequest({ ...request, description: e.target.value })}
                  />
                  <div className="char-counter">
                    {request.description.length}/{maxLength} {translations.characters}
                  </div>
                  { descSuccess && <div className="success">{descSuccess}</div> }
                  { descError && <div className="error">{descError}</div> }
                  <button 
                    type="button" 
                    className={`big-button edit ${!isEditMode ? 'active-edit' : 'inactive-edit'}`}
                    onClick={toggleEditMode}
                    disabled={isEditMode}
                  >
                    <FontAwesomeIcon icon={icons.penToSquare}/> {translations.editdesc}
                  </button>
                  <button 
                    type="button"
                    className={`big-button desc ${isEditMode ? 'active-edit' : 'inactive-edit'}`}
                    onClick={handleUpdateDesc}
                    disabled={!isEditMode}
                  >
                    <FontAwesomeIcon icon={icons.floppyDisk}/> {translations.save}
                  </button>
                  <button
                    type="button"
                    className={`big-button cancel desc ${isEditMode ? 'active-edit' : 'inactive-edit'}`}
                    onClick={handleCancel}
                    disabled={!isEditMode}
                  >
                    <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
                  </button>
                </div>
                <button className="big-button ready" onClick={() => setShowReadyConfirmation(true)}>
                  <FontAwesomeIcon icon={icons.check}/> {translations.markasready}
                </button>
                <button className="delete-request" onClick={() => setShowDeleteConfirmation(true)}>
                  <FontAwesomeIcon icon={icons.trash}/> {translations.delete}
                </button>
              </div>
              ) : (
                <div>
                  <p className="intertitle">{translations.description}</p>
                  <div className="card">{request.description}</div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="intertitle">{translations.description}</p>
              <div className="card">{request.description}</div>
              {request.responder_user_name && request.status !== 'completed' ? (
                request.responder_user_name === user?.user_name ? (
                  <button
                    type="button" 
                    className="big-button removeresponder-self"
                    onClick={() => setShowRemoveConfirmation(true)}
                  >
                    <FontAwesomeIcon icon={icons.ban}/> {translations.release}
                  </button>
                ) : (
                  <button
                    type="button" 
                    className="big-button workingonit"
                    disabled
                  >
                    {translations.workingonit}
                  </button>
                )
              ) : (
                request.status === 'new' && (
                  <div>
                    {alreadyResponded && <div className="error">{translations.errors.alreadyresponded}</div>}
                    {alreadyHasResponder && <div className="error">{translations.errors.alreadyhasresponder}</div>}
                    {limitError && <div className="error">{limitError}</div>}
                    <button
                      type="button" 
                      className="big-button takeover"
                      onClick={() => setShowAcceptConfirmation(true)}
                      disabled={isAccepted}
                    >
                      <FontAwesomeIcon icon={icons.clipboardCheck}/> {translations.takeover}
                    </button>
                    { showAcceptConfirmation && (
                      <div className="confirmation-overlay">
                        <div className="card confirm-card">
                          <p>{translations.confirmtakeover}</p>
                          <div className="confirmation-buttons">
                            <button className="big-button confirmation-cancel" onClick={() => setShowAcceptConfirmation(false)}>
                              <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
                            </button>
                            <button className="big-button confirmation-confirm takeover" onClick={handleAccept}>
                              <FontAwesomeIcon icon={icons.check}/> {translations.takeover}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
          </div>
          )}
          { isUserAdmin() && !checkIsUserRequest &&
            <div className="card admin-buttons">
              <span><FontAwesomeIcon icon={icons.lock}/> Admin:</span>
              <button className="big-button ready" onClick={() => setShowReadyConfirmation(true)}>
                <FontAwesomeIcon icon={icons.check}/> {translations.markasready}
              </button>
              <button className="big-button delete" onClick={() => setShowDeleteConfirmation(true)}>
                <FontAwesomeIcon icon={icons.trash}/> {translations.delete}
              </button>
            </div>
          }
        </PullToRefreshModalWrapper>
      ) : (
        <ModalWrapper title='-' onClose={onClose}>
          <NotFound source="modal"/>
        </ModalWrapper>
      )}
      { showReadyConfirmation && (
        <div className="confirmation-overlay">
          <div className="card confirm-card">
            <p>{translations.confirmready}</p>

            <div className="spacer1">
              <p>{translations.didyougethelp}</p>
              <label>
                <input
                  type="radio"
                  name="gothelp"
                  value="yes"
                  checked={gotHelp === true}
                  onChange={() => setGotHelp(true)}
                />
                {translations.gothelp}
              </label>
              <label style={{ marginLeft: "1rem" }}>
                <input
                  type="radio"
                  name="gothelp"
                  value="no"
                  checked={gotHelp === false}
                  onChange={() => setGotHelp(false)}
                />
                {translations.didntgethelp}
              </label>
            </div>

            <div className="confirmation-buttons">
              <button className="big-button confirmation-cancel" onClick={() => setShowReadyConfirmation(false)}>
                <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
              </button>
              <button 
                className={`big-button confirmation-confirm mark-as-ready ${gotHelp === null ? "disabled" : ""}`}
                onClick={handleReady}
                disabled={gotHelp === null}>
                <FontAwesomeIcon icon={icons.check}/> {translations.markasready}
              </button>
            </div>
          </div>
        </div>
      )}
      { showDeleteConfirmation && (
        <div className="confirmation-overlay">
          <div className="card confirm-card">
            <p>{translations.confirmdelete}</p>
            <div className="confirmation-buttons">
              <button className="big-button confirmation-cancel grey" onClick={() => setShowDeleteConfirmation(false)}>
                <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
              </button>
              <button className="big-button confirmation-confirm warning" onClick={handleDelete}>
                <FontAwesomeIcon icon={icons.trash}/> {translations.delete}
              </button>
            </div>
          </div>
        </div>
      )}
      { showRemoveConfirmation && (
        <div className="confirmation-overlay">
          <div className="card confirm-card">
            {isUserRequest ? ( <p>{translations.confirmremove}</p> ):( <p>{translations.confirmremoveself}</p>)}
            <div className="confirmation-buttons">
              <button className="big-button confirmation-cancel grey" onClick={() => setShowRemoveConfirmation(false)}>
               <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
              </button>
              <button className="big-button confirmation-confirm warning" onClick={removeResponder}>
               <FontAwesomeIcon icon={icons.ban}/> {isUserRequest ? translations.remove : translations.release}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RequestModal;